import { Loader } from "@circle-react-shared/uikit/Loader";
import { SearchInput } from "@circleco/compass/components/SearchInput";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Basic } from "unsplash-js/src/methods/photos/types";

import { t } from "@/i18n-js/instance";

import { searchUnsplash } from "../functions";

export interface UnsplashTabContentProps {
  addUnsplash: (item: Basic) => void;
}

const DEBOUNCE_MS = 350;
const DEFAULT_QUERY = "wallpapers";

export const UnsplashTabContent = ({
  addUnsplash,
}: UnsplashTabContentProps) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Basic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const abortRef = useRef(0);

  const fetchResults = useCallback(async (searchQuery: string) => {
    const requestId = ++abortRef.current;
    setIsLoading(true);

    const data = await searchUnsplash(searchQuery || DEFAULT_QUERY);

    if (requestId === abortRef.current) {
      setResults(data);
      setIsLoading(false);
      setHasSearched(true);
    }
  }, []);

  useEffect(() => {
    void fetchResults(DEFAULT_QUERY);
    return () => clearTimeout(debounceRef.current);
  }, [fetchResults]);

  const handleSearchChange = (value: string) => {
    setQuery(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void fetchResults(value);
    }, DEBOUNCE_MS);
  };

  const handleClear = () => {
    setQuery("");
    clearTimeout(debounceRef.current);
    void fetchResults("");
  };

  return (
    <>
      <div className="mb-4">
        <SearchInput
          value={query}
          placeholder={t("image_upload.search_unsplash")}
          onChange={event => handleSearchChange(event.target.value)}
          onClear={handleClear}
        />
      </div>
      <div className="max-h-96 overflow-y-auto">
        {isLoading && (
          <div className="flex h-96 items-center justify-center">
            <Loader />
          </div>
        )}
        {!isLoading && results.length === 0 && hasSearched && (
          <div className="text-light flex h-96 items-center justify-center text-sm">
            {t("search.results.zero")}
          </div>
        )}
        {!isLoading && results.length > 0 && (
          <div className="grid grid-cols-3 gap-4">
            {results.map(item => (
              <button
                type="button"
                className="cursor-pointer overflow-hidden text-left"
                key={item.id}
                onClick={() => addUnsplash(item)}
              >
                <img
                  loading="lazy"
                  className="h-32 w-full rounded-lg object-cover"
                  src={item.urls.regular}
                  alt={item.alt_description || ""}
                />
                <div className="text-light mt-1 truncate text-xs">
                  {t("image_upload.by")}{" "}
                  <a
                    href={`${item.links.html}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {item.user.first_name} {item.user.last_name}
                  </a>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
};
